// @flow
import React, { Component } from "react";
import moment from "moment";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import type { StateType, DispatchType } from "../../../global/types/redux";
import { LoadingProgress } from "imagus-components";
import UpgradeQuoteForm from "../../components/quotes/UpgradeQuoteForm";
import { buildLicenceItemJson } from "../../components/companies/CompileCompanytoJson";
import { propCompare } from "../../../global/functions/StringOperations";
import {
  CreateUpdatingQuote,
  EditUpdatingQuote,
  fetchLongTermLicence
} from "../../actions/quoteActions";
import {
  StartDatetoEndDate,
  DatabaseDateToJsDateISOString,
  TodaytoStartTimeAppend,
  isMomentBefore,
  DateDiff,
  JsDateObjectDisplayDateOnly
} from "../../../global/functions/DateTimeAmend";
import { WarningDisplay } from "../../containers/ErrosDisplay";

type UpgradeQuoteFormReduxPropsType = {
  onClose: Function,
  companyType: string,
  isAdmin?: boolean,
  quote?: *,
  licences: Array<*>,
  priceSheet: *,
  actions: {
    CreateUpdatingQuote: Function,
    EditUpdatingQuote: Function,
    fetchLongTermLicence: Function
  }
};

class UpgradeQuoteFormRedux extends Component<
  UpgradeQuoteFormReduxPropsType,
  *
> {
  componentDidMount() {
    if (!this.props.licences) {
      this.props.actions.fetchLongTermLicence();
    }
  }

  fetchLicenceDetail = (licenceId: string): * => {
    let licence = null;

    if (this.props.licences) {
      const licences = this.props.licences.map((licence: *): * =>
        buildLicenceItemJson(licence, false, true)
      );

      let index = licences.findIndex((x: *): * => x.id === licenceId);
      licence = licences[index];
    }

    return licence;
  };

  checkLicenceChanged = (licence: *): * => {
    let changed = false;
    const quote = this.props.quote;
    if (quote) {
      const licence = quote.licence;
      const parts = quote.parts;
      let index1 = parts.findIndex((x: *): * => x.partno === "IMAGUS-FR-001a");
      let index2 = parts.findIndex((x: *): * => x.partno === "IMAGUS-FR-001b");

      const quoteFaceNo = parts[index1].quantity;
      const quoteStreamNo = parts[index2].quantity;
      const quoteExpiryDate = quote.existingExpiryDateOrigin;

      const licenceFaceNo = licence.facebaseLimit;
      const licenceStreamNo = licence.noOfStreams;
      const licenceExpiryDate = licence.extentDateOrigin
        ? licence.extentDateOrigin
        : licence.expiryDateOrigin;

      if (
        quoteFaceNo !== licenceFaceNo ||
        quoteStreamNo !== licenceStreamNo ||
        quoteExpiryDate !== licenceExpiryDate
      ) {
        changed = true;
      }
    }

    return changed;
  };

  fetchRealExpiryDate = (licence: *, dateOnly?: boolean = false): * => {
    let expiry;
    if (licence) {
      if (dateOnly) {
        expiry = licence.extentDateOrigin
          ? JsDateObjectDisplayDateOnly(licence.extentDateOrigin)
          : JsDateObjectDisplayDateOnly(licence.expiryDateOrigin);
      } else {
        expiry = licence.extentDateOrigin
          ? DatabaseDateToJsDateISOString(licence.extentDateOrigin)
          : DatabaseDateToJsDateISOString(licence.expiryDateOrigin);
      }
    } else {
      expiry = null;
    }

    return expiry;
  };

  fetchExpiryDate = (licence: *, startDate: Date, yearNo: number): * => {
    const existingExpiry = this.fetchRealExpiryDate(licence);
    const newExpiry = StartDatetoEndDate(startDate, "days", 365);
    const isOverDue = isMomentBefore(existingExpiry, startDate);

    let isNewBeforeExisting = false;
    isNewBeforeExisting = isMomentBefore(newExpiry, existingExpiry);

    let renewDays;
    let finalExpiryDate;
    if (isNewBeforeExisting) {
      renewDays = DateDiff(newExpiry, existingExpiry, "days");

      finalExpiryDate = moment(existingExpiry)
        .add(parseInt(yearNo) * 365, "days")
        .utc()
        .toISOString();
    } else {
      if (isOverDue) {
        renewDays = DateDiff(startDate, newExpiry, "days");
      } else {
        renewDays = DateDiff(existingExpiry, newExpiry, "days");
      }

      finalExpiryDate = StartDatetoEndDate(
        startDate,
        "days",
        365 * (parseInt(yearNo) + 1)
      );
    }

    return [renewDays, finalExpiryDate, isOverDue];
  };

  createQuote = (
    retailSum: number,
    distributerSum: number,
    resellerSum: number,
    parts: Array<*>,
    licenceId: string,
    startDate: Date,
    expiryDate: Date,
    doneCallback: Function,
    failCallBack: Function
  ): * => {
    this.props.actions.CreateUpdatingQuote(
      retailSum,
      distributerSum,
      resellerSum,
      parts,
      licenceId,
      startDate,
      expiryDate,
      doneCallback,
      failCallBack
    );
  };

  editQuote = (
    retailSum: number,
    distributerSum: number,
    resellerSum: number,
    parts: Array<*>,
    startDate: Date,
    expiryDate: Date,
    doneCallback: Function,
    failCallBack: Function
  ): * => {
    let quoteParts = this.props.quote.parts;
    parts.map((part: *, i: number): * => {
      let index2 = -1;
      if (part.partNo.startsWith("IMQ-PR-003")) {
        index2 = quoteParts.findIndex((x) => x.partno.startsWith("IMQ-PR-003")); // special for adjusted period
      } else {
        index2 = quoteParts.findIndex((x) => x.partno === part.partNo);
      }
      part["id"] = quoteParts[index2].id;
    });

    this.props.actions.EditUpdatingQuote(
      retailSum,
      distributerSum,
      resellerSum,
      parts,
      this.props.quote.id,
      startDate,
      expiryDate,
      doneCallback,
      failCallBack
    );
  };

  render(): React$Element<*> {
    let licences;
    let LicenceList;

    if (this.props.licences) {
      licences = this.props.licences.map((licence: *): * =>
        buildLicenceItemJson(licence)
      );
      licences.sort(function (a: *, b: *): number {
        return propCompare(
          a.extentDate ? a.extentDate : a.expiryDate,
          b.extentDate ? b.extentDate : b.expiryDate
        );
      });

      LicenceList = licences.map((licence: *): * => ({
        value: licence.id,
        label:
          licence.facebaseName +
          " ( Expires In " +
          this.fetchRealExpiryDate(licence, true) +
          " )"
      }));
    }

    let initials = {};
    let isEdit = false;
    if (this.props.quote) {
      isEdit = true;
      const quote = this.props.quote;

      //initials.formIndex = 2;
      initials.licenceId = quote.licence.id;
      initials.licence = this.fetchLicenceDetail(quote.licence.id);

      const parts = quote.parts;
      let index1 = parts.findIndex((x: *): * => x.partno === "IMQ-SE-003a");
      let index2 = parts.findIndex((x: *): * => x.partno === "IMQ-SE-003b");
      /* let index3 = parts.findIndex(
        (x: *): * => x.partno.startsWith("IMQ-PR-003")
      ); */
      let index4 = parts.findIndex((x: *): * => x.partno === "IMQ-SM-003");

      initials.faceNo = parts[index1].quantity;
      initials.streamNo = parts[index2].quantity;
      /* initials.renewDays = parts[index3].quantity; */
      initials.yearNo = parts[index4].quantity;

      /*  initials.startDate = DatabaseDateToJsDateISOString(
        quote.newStartDateOrigin
      );
      initials.expiryDate = StartDatetoEndDate(
        DatabaseDateToJsDateISOString(quote.newStartDateOrigin),
        "days",
        365
      ); */
      initials.startDate = TodaytoStartTimeAppend(true);
      initials.finalExpiryDate = DatabaseDateToJsDateISOString(
        quote.newExpiryDateOrigin
      );

      initials.renewDays = this.fetchExpiryDate(
        initials.licence,
        initials.startDate,
        initials.yearNo
      )[0];
    }

    const changed = this.checkLicenceChanged();

    if (!LicenceList) {
      return <LoadingProgress />;
    } else if (changed) {
      return (
        <WarningDisplay
          warning="The perpetual licence has updated, you can NOT edit quote any more."
          display={true}
        />
      );
    } else {
      return (
        <UpgradeQuoteForm
          onClose={this.props.onClose}
          companyType={this.props.companyType}
          LicenceList={LicenceList}
          fetchLicenceDetail={this.fetchLicenceDetail}
          fetchRealExpiryDate={this.fetchRealExpiryDate}
          fetchExpiryDate={this.fetchExpiryDate}
          initials={initials}
          isAdmin={this.props.isAdmin}
          isEdit={isEdit}
          priceSheet={this.props.priceSheet}
          createQuote={this.createQuote}
          editQuote={this.editQuote}
        />
      );
    }
  }
}

const mapStateToProps = (state: StateType): * => ({
  licences: state.v3account.quote.licences,
  priceSheet: state.v3account.quote.priceSheet
});

const mapDispatchToProps = (dispatch: DispatchType): DispatchType => ({
  actions: bindActionCreators(
    { CreateUpdatingQuote, EditUpdatingQuote, fetchLongTermLicence },
    dispatch
  )
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UpgradeQuoteFormRedux);
